import {
  GeoJsonType,
  LocationQuestionConfig,
  LocationResponseState,
  useCurrentLocation,
} from '@gripp/shared-logic';
import { useCallback } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { TextInput } from '../../../../crop-circle/Components/TextInput';
import { LocationIcon } from '../../../../crop-circle/icons';
import { colors, spacing } from '../../../../crop-circle/tokens';
import { useFormContext } from '../../../../forms';
import {
  RoutineQuestionExecutionForm,
  RoutineQuestionExecutionFormProps,
} from '../types';

const formatCoordinates = (coordinates: [number, number]): string => {
  const [longitude, latitude] = coordinates;
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};

export const LocationQuestionExecutionForm: RoutineQuestionExecutionForm<
  LocationQuestionConfig,
  LocationResponseState
> = (
  props: RoutineQuestionExecutionFormProps<
    LocationQuestionConfig,
    LocationResponseState
  >
) => {
  const { t } = useTranslation();
  const fieldPrefix = `responses.${props.responseIndex}`;
  const { control, setValue } = useFormContext();
  const { getLocation } = useCurrentLocation();

  const priorResponse: LocationResponseState | undefined =
    props?.isIssueEditRoutine
      ? (props?.currentState?.state as any)?.responses?.[props.responseIndex]
      : undefined;

  const { field } = useController({
    control,
    name: `${fieldPrefix}.value`,
    defaultValue: priorResponse?.value ?? '',
  });

  useController({
    control,
    name: `${fieldPrefix}.coordinates`,
    defaultValue: priorResponse?.coordinates ?? undefined,
  });

  const { field: memoField } = useController({
    control,
    name: `${fieldPrefix}.memo`,
    defaultValue: priorResponse?.memo ?? '',
  });

  const handleCapture = useCallback(async () => {
    const location = await getLocation();
    if (
      location?.type === GeoJsonType.Point &&
      Array.isArray(location.coordinates) &&
      location.coordinates.length === 2 &&
      typeof location.coordinates[0] === 'number' &&
      typeof location.coordinates[1] === 'number'
    ) {
      const coords = location.coordinates as [number, number];
      const formatted = formatCoordinates(coords);
      setValue(`${fieldPrefix}.value`, formatted, { shouldDirty: true });
      setValue(`${fieldPrefix}.coordinates`, coords, { shouldDirty: true });
    }
  }, [getLocation, setValue, fieldPrefix]);

  const rightIcon = (
    <Pressable
      onPress={handleCapture}
      hitSlop={spacing.sm}
      accessibilityRole="button"
      accessibilityLabel={t(
        'routines.workflow.execution.location.captureLabel'
      )}
    >
      <LocationIcon size={spacing.md} color={colors.neutral[500]} />
    </Pressable>
  );

  return (
    <View style={styles.container}>
      <TextInput
        value={field.value || ''}
        placeholder={t('routines.workflow.execution.location.placeholder')}
        disabled={true}
        clearable={false}
        rightIcon={rightIcon}
        testID="location-question-input"
      />
      {props.config.includeMemo && (
        <TextInput
          value={memoField.value || ''}
          onChangeText={memoField.onChange}
          onBlur={memoField.onBlur}
          placeholder={t(
            'routines.workflow.execution.location.memoPlaceholder'
          )}
          clearable={false}
          textArea
          autoExpand
          testID="location-question-memo"
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: spacing.sm,
  },
});
